"use client";

import { Link } from "@/i18n/navigation";
import { useTranslations } from "next-intl";
import { Button } from "@/components/ui/button";
import { motion } from "framer-motion";
import { useTheme } from "next-themes";
import { useEffect, useState } from "react";
import { ArrowLeft, Home, Search, Compass } from "lucide-react";

const floatingShapes = [
  { size: 60, x: "15%", y: "20%", delay: 0, duration: 6 },
  { size: 40, x: "80%", y: "15%", delay: 1, duration: 8 },
  { size: 80, x: "70%", y: "70%", delay: 0.5, duration: 7 },
  { size: 30, x: "20%", y: "75%", delay: 2, duration: 5 },
  { size: 50, x: "50%", y: "10%", delay: 1.5, duration: 9 },
];

const glitchSteps = [
  { x: -2, y: 0, opacity: 0.8 },
  { x: 3, y: -1, opacity: 0.6 },
  { x: -1, y: 2, opacity: 0.7 },
  { x: 2, y: -2, opacity: 0.9 },
  { x: 0, y: 0, opacity: 1 },
];

export default function NotFound() {
  const t = useTranslations("pageNotFound");
  const { resolvedTheme } = useTheme();
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  return (
    <div className="relative flex min-h-screen flex-col items-center justify-center overflow-hidden bg-background">
      {/* Animated mesh gradient background */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute -inset-[100%] opacity-30 dark:opacity-20">
          <div className="absolute left-1/2 top-1/3 h-[500px] w-[500px] -translate-x-1/2 -translate-y-1/2 rounded-full bg-primary/20 blur-[120px] animate-pulse" />
          <div className="absolute left-1/3 top-2/3 h-[400px] w-[400px] -translate-x-1/2 -translate-y-1/2 rounded-full bg-blue-500/15 blur-[100px] animate-pulse [animation-delay:2s]" />
          <div className="absolute right-1/3 top-1/4 h-[350px] w-[350px] -translate-x-1/2 -translate-y-1/2 rounded-full bg-violet-500/15 blur-[90px] animate-pulse [animation-delay:4s]" />
        </div>

        {/* Grid pattern overlay */}
        <div
          className="absolute inset-0 opacity-[0.03] dark:opacity-[0.05]"
          style={{
            backgroundImage:
              "linear-gradient(rgba(255,255,255,.1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,.1) 1px, transparent 1px)",
            backgroundSize: "60px 60px",
          }}
        />
      </div>

      {/* Floating shapes */}
      {mounted &&
        floatingShapes.map((shape, i) => (
          <motion.div
            key={i}
            className="absolute rounded-full border border-primary/10 bg-primary/5 dark:bg-primary/[0.03]"
            style={{
              width: shape.size,
              height: shape.size,
              left: shape.x,
              top: shape.y,
            }}
            animate={{
              y: [0, -20, 0],
              rotate: [0, 180, 360],
              scale: [1, 1.1, 1],
            }}
            transition={{
              duration: shape.duration,
              delay: shape.delay,
              repeat: Infinity,
              ease: "easeInOut",
            }}
          />
        ))}

      {/* Content */}
      <div className="relative z-10 mx-auto flex max-w-lg flex-col items-center px-4 text-center">
        {/* Large 404 with gradient and animation */}
        <motion.div
          initial={{ opacity: 0, scale: 0.5, y: 40 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          transition={{ duration: 0.6, ease: "easeOut" }}
          className="mb-2 select-none"
        >
          <div className="relative">
            <span className="text-[140px] sm:text-[180px] font-black leading-none tracking-tighter bg-gradient-to-r from-primary via-blue-500 to-violet-500 bg-clip-text text-transparent">
              404
            </span>
            {/* Glitch overlay */}
            <motion.div
              className="absolute inset-0 flex items-center justify-center"
              aria-hidden
            >
              {glitchSteps.map((step, i) => (
                <motion.span
                  key={i}
                  className="absolute text-[140px] sm:text-[180px] font-black leading-none tracking-tighter bg-gradient-to-r from-primary via-blue-500 to-violet-500 bg-clip-text text-transparent"
                  initial={false}
                  animate={step}
                  transition={{
                    duration: 0.05,
                    delay: 3 + i * 0.1,
                    repeat: i === 4 ? 0 : 1,
                    repeatDelay: 8,
                  }}
                >
                  404
                </motion.span>
              ))}
            </motion.div>
          </div>
        </motion.div>

        {/* Decorative line */}
        <motion.div
          initial={{ scaleX: 0 }}
          animate={{ scaleX: 1 }}
          transition={{ duration: 0.5, delay: 0.3 }}
          className="mb-6 h-[2px] w-24 origin-center rounded-full bg-gradient-to-r from-transparent via-primary/50 to-transparent"
        />

        {/* Title */}
        <motion.h1
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.4 }}
          className="mb-3 text-2xl font-bold sm:text-3xl"
        >
          {t("title")}
        </motion.h1>

        {/* Description */}
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.5 }}
          className="mb-8 max-w-sm text-muted-foreground"
        >
          {t("description")}
        </motion.p>

        {/* Action buttons */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.6 }}
          className="flex flex-wrap items-center justify-center gap-3"
        >
          <Button asChild size="lg" className="gap-2">
            <Link href="/">
              <Home className="h-4 w-4" />
              {t("backToHome")}
            </Link>
          </Button>
          <Button variant="outline" size="lg" asChild className="gap-2">
            <Link href="/models">
              <Compass className="h-4 w-4" />
              {t("browseModels")}
            </Link>
          </Button>
        </motion.div>

        {/* Helpful links */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.5, delay: 0.8 }}
          className="mt-12 flex items-center gap-2 text-sm text-muted-foreground/60"
        >
          <span>{t("tryThese")}</span>
          <Link
            href="/platform/account"
            className="underline underline-offset-4 transition hover:text-foreground"
          >
            {t("account")}
          </Link>
          <span>&middot;</span>
          <Link
            href="/platform/keys"
            className="underline underline-offset-4 transition hover:text-foreground"
          >
            {t("apiKeys")}
          </Link>
          <span>&middot;</span>
          <Link
            href="/platform/recharge/history"
            className="underline underline-offset-4 transition hover:text-foreground"
          >
            {t("billing")}
          </Link>
        </motion.div>
      </div>
    </div>
  );
}
